import { useState, useRef, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAwarenessImages, useAwarenessCategories, type AwarenessImage } from '@/hooks/use-supabase-data'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Image as ImageIcon,
  Plus,
  Trash2,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Upload,
  Edit,
  Lightbulb,
  Search,
  ChevronDown,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'

interface ImageUploadItem {
  id: string
  file: File
  preview: string
  uploading: boolean
  uploaded: boolean
  imageUrl?: string
  error?: string
}

export function AwarenessManager() {
  const { images, loading, error, refetch } = useAwarenessImages(undefined, true) // Include inactive images in admin
  const { categories: existingCategories, loading: categoriesLoading } = useAwarenessCategories(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [uploadQueue, setUploadQueue] = useState<ImageUploadItem[]>([])
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')
  const [uploadCategory, setUploadCategory] = useState('')
  const [categorySearch, setCategorySearch] = useState('')
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingImage, setEditingImage] = useState<AwarenessImage | null>(null)
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: '',
    is_active: true,
  })
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState('')
  const [editCategorySearch, setEditCategorySearch] = useState('')
  const [isEditCategoryDropdownOpen, setIsEditCategoryDropdownOpen] = useState(false)
  const [showCustomCategory, setShowCustomCategory] = useState(false)
  const [showEditCustomCategory, setShowEditCustomCategory] = useState(false)
  const editCategoryDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCategoryDropdownOpen(false)
        setCategorySearch('')
      }
      if (
        editCategoryDropdownRef.current &&
        !editCategoryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEditCategoryDropdownOpen(false)
        setEditCategorySearch('')
      }
    }

    if (isCategoryDropdownOpen || isEditCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCategoryDropdownOpen, isEditCategoryDropdownOpen])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const newItems: ImageUploadItem[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(2)}`,
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      uploaded: false,
    }))

    setUploadQueue((prev) => [...prev, ...newItems])
    setIsUploadDialogOpen(true)
    
    // Reset file input
    if (e.target) {
      e.target.value = ''
    }
  }

  const handleRemoveFromQueue = (id: string) => {
    setUploadQueue((prev) => {
      const item = prev.find((i) => i.id === id)
      if (item && !item.uploaded) {
        URL.revokeObjectURL(item.preview)
      }
      return prev.filter((i) => i.id !== id)
    })
  }

  const uploadImage = async (item: ImageUploadItem): Promise<string | null> => {
    try {
      // Upload to Supabase Storage
      const fileExt = item.file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `awareness/${fileName}`

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('village-portal-images')
        .upload(filePath, item.file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('village-portal-images')
        .getPublicUrl(filePath)

      return urlData.publicUrl
    } catch (err: any) {
      throw new Error(err.message || 'Failed to upload image')
    }
  }

  const handleUploadAll = async () => {
    if (uploadQueue.length === 0) return

    setStatusMessage('')
    setStatusType('')

    // Upload images one by one
    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i]
      
      // Skip if already uploaded
      if (item.uploaded) continue

      // Mark as uploading
      setUploadQueue((prev) =>
        prev.map((img) =>
          img.id === item.id ? { ...img, uploading: true, error: undefined } : img
        )
      )

      try {
        const imageUrl = await uploadImage(item)
        if (!imageUrl) throw new Error('Failed to get image URL')

        // Save to database
        const { error: dbError } = await supabase.from('awareness_images').insert({
          image_url: imageUrl,
          title: null,
          description: null,
          category: uploadCategory.trim() || null,
          display_order: 0,
          is_active: true,
          created_at: new Date().toISOString(),
        })

        if (dbError) throw dbError

        // Mark as uploaded
        setUploadQueue((prev) =>
          prev.map((img) =>
            img.id === item.id
              ? { ...img, uploading: false, uploaded: true, imageUrl }
              : img
          )
        )
      } catch (err: any) {
        setUploadQueue((prev) =>
          prev.map((img) =>
            img.id === item.id
              ? { ...img, uploading: false, error: err.message || 'Upload failed' }
              : img
          )
        )
      }
    }

    // Check if all uploaded successfully
    const allUploaded = uploadQueue.every((item) => item.uploaded || item.error)
    if (allUploaded) {
      const successCount = uploadQueue.filter((item) => item.uploaded).length
      if (successCount > 0) {
        setStatusType('success')
        setStatusMessage(`${successCount} image(s) uploaded successfully.`)
        await refetch()
        
        // Clear queue after a delay
        setTimeout(() => {
          setUploadQueue([])
          setIsUploadDialogOpen(false)
          setUploadCategory('')
        }, 2000)
      }
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('awareness_images').delete().eq('id', id)
      if (error) throw error
      setStatusType('success')
      setStatusMessage('Image deleted successfully.')
      await refetch()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage('Failed to delete image.')
    }
  }

  const handleEdit = (image: AwarenessImage) => {
    setEditingImage(image)
    setEditFormData({
      title: image.title || '',
      description: image.description || '',
      category: image.category || '',
      is_active: image.is_active ?? true,
    })
    const category = image.category || ''
    setShowEditCustomCategory(category !== '' && !existingCategories.includes(category))
    setIsEditDialogOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!editingImage) return

    setSavingEdit(true)
    setEditError('')

    try {
      const { error: dbError } = await supabase.from('awareness_images').update({
        title: editFormData.title.trim() || null,
        description: editFormData.description.trim() || null,
        category: editFormData.category.trim() || null,
        is_active: editFormData.is_active,
        updated_at: new Date().toISOString(),
      }).eq('id', editingImage.id)

      if (dbError) throw dbError

      setStatusType('success')
      setStatusMessage('Image updated successfully.')
      await refetch()
      setIsEditDialogOpen(false)
    } catch (err: any) {
      setEditError(err.message || 'Failed to update image.')
    } finally {
      setSavingEdit(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">
            Awareness Management
          </h2>
          <p className="text-slate-600 text-lg">Upload and manage awareness images by category</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full sm:w-auto bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Images
            </Button>
          </div>
        </div>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : statusType === 'error'
                ? undefined
                : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {/* Images Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Awareness Images ({images.length})
          </CardTitle>
          <CardDescription>Manage your awareness images</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mr-3" />
              Loading images...
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-10">
              <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No images found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded Date</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {images.map((image) => (
                  <TableRow key={image.id}>
                    <TableCell>
                      <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-border flex-shrink-0">
                        <img
                          src={image.image_url}
                          alt="Awareness image"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{image.title || 'N/A'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">{image.category || 'Uncategorized'}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={image.is_active ? "default" : "outline"} className={image.is_active ? "bg-emerald-500 text-white" : "text-muted-foreground"}>
                        {image.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(image.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(image)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete image?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove this image from the awareness section.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600"
                                onClick={() => handleDelete(image.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Queue Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Awareness Images</DialogTitle>
            <DialogDescription>
              {uploadQueue.length} image(s) selected. Images will be uploaded one by one.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="upload-category">Category</Label>
              <div className="relative" ref={categoryDropdownRef}>
                {!showCustomCategory ? (
                  <div className="relative">
                    <div
                      className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-muted/50"
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    >
                      <span className={uploadCategory ? 'text-foreground' : 'text-muted-foreground'}>
                        {uploadCategory || 'Select or type a category (optional)'}
                      </span>
                      <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                    {isCategoryDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search categories..."
                              value={categorySearch}
                              onChange={(e) => setCategorySearch(e.target.value)}
                              className="pl-8 h-8"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {existingCategories
                            .filter((cat) =>
                              cat.toLowerCase().includes(categorySearch.toLowerCase())
                            )
                            .map((cat) => (
                              <div
                                key={cat}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                                onClick={() => {
                                  setUploadCategory(cat)
                                  setIsCategoryDropdownOpen(false)
                                  setCategorySearch('')
                                }}
                              >
                                {cat}
                              </div>
                            ))}
                          {existingCategories.filter((cat) =>
                            cat.toLowerCase().includes(categorySearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              No categories found
                            </div>
                          )}
                          <div
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 border-t"
                            onClick={() => {
                              setShowCustomCategory(true)
                              setIsCategoryDropdownOpen(false)
                              setCategorySearch('')
                            }}
                          >
                            + Add New Category
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      id="upload-category"
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      placeholder="Type new category name"
                      list="category-suggestions-upload"
                    />
                    <datalist id="category-suggestions-upload">
                      {existingCategories.map((cat) => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowCustomCategory(false)
                        setUploadCategory('')
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {uploadQueue.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadQueue.map((item) => (
                  <div key={item.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-border relative">
                      <img
                        src={item.preview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      {item.uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <RefreshCw className="w-6 h-6 text-white animate-spin" />
                        </div>
                      )}
                      {item.uploaded && (
                        <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        </div>
                      )}
                      {item.error && (
                        <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                          <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                      )}
                      {!item.uploading && !item.uploaded && (
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                          onClick={() => handleRemoveFromQueue(item.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {item.error && (
                      <p className="text-xs text-red-600 mt-1 truncate" title={item.error}>
                        {item.error}
                      </p>
                    )}
                    {item.uploaded && (
                      <p className="text-xs text-emerald-600 mt-1">Uploaded</p>
                    )}
                    {item.uploading && (
                      <p className="text-xs text-blue-600 mt-1">Uploading...</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  uploadQueue.forEach((item) => {
                    if (!item.uploaded) {
                      URL.revokeObjectURL(item.preview)
                    }
                  })
                  setUploadQueue([])
                  setIsUploadDialogOpen(false)
                  setUploadCategory('')
                  setShowCustomCategory(false)
                }}
              >
                {uploadQueue.some((item) => item.uploaded) ? 'Close' : 'Cancel'}
              </Button>
              {uploadQueue.length > 0 && !uploadQueue.every((item) => item.uploaded) && (
                <Button
                  onClick={handleUploadAll}
                  disabled={uploadQueue.some((item) => item.uploading)}
                  className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadQueue.some((item) => item.uploading)
                    ? 'Uploading...'
                    : `Upload ${uploadQueue.filter((item) => !item.uploaded).length} Image(s)`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Image Dialog */}
      {editingImage && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Awareness Image</DialogTitle>
              <DialogDescription>Update image details and status.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border">
                <img src={editingImage.image_url} alt="Current image" className="w-full h-full object-cover" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  placeholder="Enter image title (optional)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  placeholder="Enter image description (optional)"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <div className="relative" ref={editCategoryDropdownRef}>
                  {!showEditCustomCategory ? (
                    <div className="relative">
                      <div
                        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-muted/50"
                        onClick={() => setIsEditCategoryDropdownOpen(!isEditCategoryDropdownOpen)}
                      >
                        <span className={editFormData.category ? 'text-foreground' : 'text-muted-foreground'}>
                          {editFormData.category || 'Select or type a category (optional)'}
                        </span>
                        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isEditCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                      </div>
                      {isEditCategoryDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md">
                          <div className="p-2 border-b">
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search categories..."
                                value={editCategorySearch}
                                onChange={(e) => setEditCategorySearch(e.target.value)}
                                className="pl-8 h-8"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {existingCategories
                              .filter((cat) =>
                                cat.toLowerCase().includes(editCategorySearch.toLowerCase())
                              )
                              .map((cat) => (
                                <div
                                  key={cat}
                                  className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                                  onClick={() => {
                                    setEditFormData({ ...editFormData, category: cat })
                                    setIsEditCategoryDropdownOpen(false)
                                    setEditCategorySearch('')
                                  }}
                                >
                                  {cat}
                                </div>
                              ))}
                            {existingCategories.filter((cat) =>
                              cat.toLowerCase().includes(editCategorySearch.toLowerCase())
                            ).length === 0 && (
                              <div className="px-3 py-2 text-sm text-muted-foreground">
                                No categories found
                              </div>
                            )}
                            <div
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 border-t"
                              onClick={() => {
                                setShowEditCustomCategory(true)
                                setIsEditCategoryDropdownOpen(false)
                                setEditCategorySearch('')
                              }}
                            >
                              + Add New Category
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        id="edit-category"
                        value={editFormData.category}
                        onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                        placeholder="Type new category name"
                        list="category-suggestions-edit"
                      />
                      <datalist id="category-suggestions-edit">
                        {existingCategories.map((cat) => (
                          <option key={cat} value={cat} />
                        ))}
                      </datalist>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowEditCustomCategory(false)
                          setEditFormData({ ...editFormData, category: '' })
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-is_active">Status</Label>
                <div className="flex items-center gap-2 rounded-md border border-input px-3 py-2">
                  <Switch
                    id="edit-is_active"
                    checked={editFormData.is_active}
                    onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_active: checked })}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                  <span className={`text-sm ${editFormData.is_active ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {editFormData.is_active ? 'Active (Visible on site)' : 'Inactive (Hidden from site)'}
                  </span>
                </div>
              </div>
              {editError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{editError}</AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={savingEdit} className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600">
                  {savingEdit ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
